import React from 'react';
import ArchiveOutlinedIcon from '@mui/icons-material/ArchiveOutlined';
import PublishOutlinedIcon from '@mui/icons-material/PublishOutlined';
import MyBriefsExpandedContent from './MyBriefsExpandedContent';
import {
  formatRelativeTime,
  formatStatusLabel,
  isActiveStatus,
  isAssembledContentType,
  contentTypeChipLabel,
  untitledLabelFor,
} from './myBriefsUtils';

/**
 * Compact summary chip shown in the collapsed row when this draft has been
 * published to one or more CMS integrations. Hover/tap exposes the full list
 * inside the expanded view; the chip itself is informational only so it
 * doesn't steal the row's click target.
 */
function CmsPublicationsChip({ publications }) {
  if (!Array.isArray(publications) || publications.length === 0) return null;
  const count = publications.length;
  const first = publications[0];
  // Single publication → show "<Integration> #<externalId>"; multiple → count summary.
  const label = count === 1
    ? `${first?.clientName || 'CMS'}${first?.externalId ? ` #${first.externalId}` : ''}`
    : `${count} CMS publications`;
  const titleAttr = publications
    .map((p) => `${p?.clientName || 'CMS'}${p?.externalId ? ` #${p.externalId}` : ''}`)
    .join(' • ');
  return (
    <span className="my-briefs-cms-chip" title={titleAttr} data-testid="my-briefs-cms-chip">
      <span className="my-briefs-cms-chip-icon" aria-hidden="true">
        <PublishOutlinedIcon style={{ fontSize: 12 }} />
      </span>
      {label}
    </span>
  );
}

function MyBriefsListItem({
  item,
  nowMs,
  showOwnerMeta,
  canManage,
  isExpanded,
  onToggle,
  detail,
  detailLoading,
  editDraft,
  comparisonView,
  isDirty,
  isSaving,
  saveStatus,
  onComparisonViewChange,
  onEditFieldChange,
  onSave,
  onCopy,
  onPublishCms,
  onSendToClient,
  onRegenerate,
  onRetry,
  onDelete,
  onRestore,
  onPermanentDelete,
  onOpenTranslate,
  isTranslating,
}) {
  const resolvedStatus = detail?.status || item.status;
  // The list projects contentType; the detail carries it too once expanded.
  const contentType = detail?.contentType || item.contentType;
  const isNewsletter = contentType === 'newsletter';
  // Newsletter-only above (the item-order panel is a newsletter concept);
  // assembled covers media analyses too, for the action and metadata gating.
  const isAssembled = isAssembledContentType(contentType);
  const chipLabel = contentTypeChipLabel(contentType);
  const relativeTime = formatRelativeTime(item.createdAt, nowMs);
  // Prefer the freshest publication list — detail (refreshed on expand)
  // when present, otherwise the snapshot that came with the list response.
  const cmsPublications = (detail?.cmsPublications && detail.cmsPublications.length > 0)
    ? detail.cmsPublications
    : (item.cmsPublications || []);
  // Treat an in-flight translation like the existing in-progress states:
  // collapse the row and show a "Translating..." spinner in the header.
  const canExpand = !isActiveStatus(resolvedStatus) && !isTranslating;
  const toggleLabel = isExpanded ? 'Collapse draft' : 'Expand draft';
  const showArchiveAction = canManage && (resolvedStatus === 'ready' || resolvedStatus === 'failed');

  return (
    <article
      id={`saved-brief-${item._id}`}
      className={`my-briefs-list-item status-${resolvedStatus}${isExpanded ? ' is-expanded' : ''}${canExpand ? '' : ' is-non-expandable'}`}
      data-testid={`my-brief-${item._id}`}
    >
      <div className="my-briefs-list-item-header-row">
        <button
          type="button"
          className="my-briefs-list-item-header"
          onClick={() => {
            if (canExpand) onToggle(item._id);
          }}
          aria-expanded={canExpand ? isExpanded : undefined}
          aria-label={canExpand ? toggleLabel : `${formatStatusLabel(resolvedStatus)} draft`}
        >
          <span
            className={`sf-row-chevron ${isExpanded ? ' sf-row-chevron-open' : ''}${canExpand ? '' : ' my-briefs-row-chevron-disabled'}`}
            aria-hidden="true"
            data-testid={`my-brief-caret-${item._id}`}
          >
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
              <polyline points="9 18 15 12 9 6" />
            </svg>
          </span>
          <div className="my-briefs-list-item-title">
            {item.storyTitle || untitledLabelFor(contentType)}
          </div>
          <div className="my-briefs-list-item-meta">
            {/* An assembled artifact has no single originating story, so the
                story-shaped metadata below (article count) is meaningless for
                it. Reuses the newsletter chip's existing styling rather than
                inventing a second accent colour. */}
            {chipLabel ? <span className="my-briefs-newsletter-chip">{chipLabel}</span> : null}
            <span>{relativeTime}</span>
            {isActiveStatus(resolvedStatus) || isTranslating ? (
              <span className="my-briefs-inline-spinner">
                <span className="my-briefs-inline-spinner-dot" aria-hidden="true" />
                {isTranslating
                  ? 'Translating...'
                  : resolvedStatus === 'regenerating'
                    ? 'Regenerating...'
                    : 'Generating...'}
              </span>
            ) : (
              <span>{formatStatusLabel(resolvedStatus)}</span>
            )}
            <span>{item.language || 'Unknown language'}</span>
            {isAssembled ? null : <span>{item.selectedArticleCount || 0} articles</span>}
            <CmsPublicationsChip publications={cmsPublications} />
            {showOwnerMeta && (item.userName || item.userEmail) ? <span className="my-briefs-owner-meta">Owner: {item.userName || item.userEmail}</span> : null}
          </div>
        </button>

        {showArchiveAction ? (
          <button
            type="button"
            className="my-briefs-action-button my-briefs-inline-archive-button"
            onClick={onDelete}
            data-testid={`my-brief-inline-archive-${item._id}`}
          >
            <span className="my-briefs-action-button-icon" aria-hidden="true">
              <ArchiveOutlinedIcon style={{ fontSize: 16 }} />
            </span>
            Archive
          </button>
        ) : null}
      </div>

      {isExpanded ? (
        <div className="my-briefs-expanded-wrap my-briefs-expanded-wrap-full-height" data-testid={`my-brief-expanded-wrap-${item._id}`}>
          <MyBriefsExpandedContent
            item={item}
            detail={detail}
            canManage={canManage}
            isLoading={detailLoading}
            editDraft={editDraft}
            comparisonView={comparisonView}
            isDirty={isDirty}
            isSaving={isSaving}
            saveStatus={saveStatus}
            onComparisonViewChange={onComparisonViewChange}
            onEditFieldChange={onEditFieldChange}
            onSave={onSave}
            onCopy={onCopy}
            onPublishCms={onPublishCms}
            onSendToClient={onSendToClient}
            onRegenerate={onRegenerate}
            onRetry={onRetry}
            onDelete={onDelete}
            onRestore={onRestore}
            onPermanentDelete={onPermanentDelete}
            onOpenTranslate={onOpenTranslate}
            isNewsletter={isNewsletter}
            isAssembled={isAssembled}
          />
        </div>
      ) : null}
    </article>
  );
}

export default MyBriefsListItem;
